import {type ChangeEventHandler, useCallback, useState} from 'react';
import {useSelector} from 'react-redux';

import {generateNewKey} from 'src/openapi/admin/apis/KeyControllerApi';
import {crmIntegration, enabled, fullAccess, getSettings} from 'src/openapi/admin/apis/SettingsControllerApi';
import {KeyType} from 'src/openapi/admin/models/KeyType';
import {useAppDispatch} from 'src/store/hooks';

import {getAccountId} from '../../../../services/selectors';
import {CONFIRM} from '../constants';

import type {ConfirmTypes} from '../constants';

export const useConfirmActions = () => {
    const apiDispatch = useAppDispatch();
    const accountId = useSelector(getAccountId);

    const [busy, setBusy] = useState(false);

    const onToggleApi = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const val = event.currentTarget.checked;
        apiDispatch(enabled({accountId, enabled: val})).then(resp => {
            if (resp.payload.success) {
                apiDispatch(getSettings({accountId}));
            }
        });
    }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

    const onToggleCrmIntegration = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const val = event.currentTarget.checked;
        apiDispatch(crmIntegration({accountId, crmIntegration: val})).then(resp => {
            if (resp.payload.success) {
                apiDispatch(getSettings({accountId}));
            }
        });
    }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

    const confirmFullAccess = useCallback(async (access: boolean) => {
        setBusy(true);
        const response = await apiDispatch(fullAccess({accountId, fullAccess: access}));
        if (response.payload.success) {
            apiDispatch(getSettings({accountId}));
        }
        return response;
    }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

    const confirmGenerateKey = useCallback(async (keyType: KeyType) => {
        setBusy(true);
        const response = await apiDispatch(generateNewKey({accountId, keyType}));
        if (response.payload.success) {
            apiDispatch(getSettings({accountId}));
        }
        return response;
    }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

    const onConfirm = useCallback(async (confirmType: ConfirmTypes, value?: boolean) => {
        switch (confirmType) {
            case CONFIRM.DISABLE_FULL_ACCESS:
                await confirmFullAccess(!!value);
                break;
            case CONFIRM.GENERATE_API_KEY:
                await confirmGenerateKey(KeyType.FULL_KEY);
                break;
            case CONFIRM.GENERATE_SANDBOX_KEY:
                await confirmGenerateKey(KeyType.DEV_KEY);
                break;
            default:
                break;
        }
    }, [confirmFullAccess, confirmGenerateKey]);

    return {
        onToggleApi,
        onToggleCrmIntegration,
        onConfirm,
        busy,
    };
};
